import { auth, db } from '@/api/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

// ----- Context yaratish -----
const MainContext = createContext({});
export const useMainContext = () => useContext(MainContext);

// ----- Initial state -----
const initialState = {
  isOpen: true,
  loading: true,
  user: null,
  uid: null,
  activeBranchId: localStorage.getItem('activeBranchId') || 'default',
  userData: null,
  courses: [],
  teachers: [],
  groups: [],
  students: [],
  paymentHistory: [],
  branches: [],
  rooms: [], // 🔥 qo‘shildi
  openStudentEdit: false,
  openStudentDelete: false,
  openStudentPayment: false,
};

// ----- Reducer -----
function reducer(state, action) {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_UID':
      return { ...state, uid: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ACTIVE_BRANCH':
      localStorage.setItem('activeBranchId', action.payload || '');
      return { ...state, activeBranchId: action.payload };

    case 'SET_USER_DATA':
      return { ...state, userData: action.payload };

    case 'SET_COURSES':
      return { ...state, courses: action.payload };
    case 'SET_TEACHERS':
      return { ...state, teachers: action.payload };
    case 'SET_GROUPS':
      return { ...state, groups: action.payload };
    case 'SET_STUDENTS':
      return { ...state, students: action.payload };
    case 'SET_PAYMENT_HISTORY':
      return { ...state, paymentHistory: action.payload };
    case 'SET_BRANCHES':
      return { ...state, branches: action.payload };
    case 'SET_ROOMS': // 🔥 qo‘shildi
      return { ...state, rooms: action.payload };

    case 'TOGGLE_MODAL':
      return { ...state, [action.payload.modal]: action.payload.value };
    default:
      return state;
  }
}

// ----- Custom hook: collection data -----
const useCollection = (path, uid, queryFn, deps = []) => {
  const [data, setData] = React.useState([]);

  useEffect(() => {
    if (!uid) return;

    const colRef = collection(db, path);
    const q = queryFn ? queryFn(colRef) : colRef;

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setData(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    });

    return unsubscribe;
  }, [path, uid, ...deps]);

  return data;
};

// ----- MainContextProvider -----
export const MainContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // ----- Auth listener -----
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      dispatch({ type: 'SET_USER', payload: user });
      dispatch({ type: 'SET_UID', payload: user?.uid || null });
      dispatch({ type: 'SET_LOADING', payload: false });
    });
    return unsubscribe;
  }, []);

  // ----- userData listener -----
  useEffect(() => {
    if (!state.uid) {
      dispatch({ type: 'SET_USER_DATA', payload: null });
      return;
    }

    const userDocRef = doc(db, 'users', state.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        dispatch({
          type: 'SET_USER_DATA',
          payload: { id: docSnap.id, ...docSnap.data() },
        });
      } else {
        dispatch({ type: 'SET_USER_DATA', payload: null });
      }
    });

    return () => unsubscribe();
  }, [state.uid]);

  // ----- Firebase collections (real-time) -----
  const courses = useCollection(
    `users/${state.uid}/courses`,
    state.uid,
    state.activeBranchId
      ? (colRef) => query(colRef, where('branchId', '==', state.activeBranchId))
      : null,
    [state.activeBranchId]
  );

  const teachers = useCollection(
    `teachers`,
    state.uid,
    state.activeBranchId
      ? (colRef) =>
          query(
            colRef,
            where('branchId', '==', state.activeBranchId),
            where('role', '==', state.uid)
          )
      : (colRef) => query(colRef, where('role', '==', state.uid)),
    [state.activeBranchId]
  );

  const students = useCollection(
    `students`,
    state.uid,
    state.activeBranchId
      ? (colRef) =>
          query(
            colRef,
            where('branchId', '==', state.activeBranchId),
            where('adminId', '==', state.uid)
          )
      : (colRef) => query(colRef, where('adminId', '==', state.uid)),
    [state.activeBranchId]
  );

  const groups = useCollection(
    `users/${state.uid}/groups`,
    state.uid,
    state.activeBranchId
      ? (colRef) => query(colRef, where('branchId', '==', state.activeBranchId))
      : null,
    [state.activeBranchId]
  );

  const paymentHistory = useCollection(
    `users/${state.uid}/paymentHistory`,
    state.uid,
    state.activeBranchId
      ? (colRef) => query(colRef, where('branchId', '==', state.activeBranchId))
      : null,
    [state.activeBranchId]
  );

  const branches = useCollection(`users/${state.uid}/branches`, state.uid);

  // ----- 🔥 Rooms faqat getDocs bilan -----
  useEffect(() => {
    const fetchRooms = async () => {
      if (!state.uid || !state.activeBranchId) return;
      try {
        const roomsColRef = collection(db, `users/${state.uid}/rooms`);
        const q = query(
          roomsColRef,
          where('branchId', '==', state.activeBranchId)
        );
        const querySnapshot = await getDocs(q);
        const roomsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        dispatch({ type: 'SET_ROOMS', payload: roomsData });
      } catch (error) {
        console.error('Error fetching rooms:', error);
      }
    };

    fetchRooms();
  }, [state.uid, state.activeBranchId]);

  // ----- Sync collection data to state -----
  useEffect(() => {
    dispatch({ type: 'SET_COURSES', payload: courses });
  }, [courses]);
  useEffect(() => {
    dispatch({ type: 'SET_TEACHERS', payload: teachers });
  }, [teachers]);
  useEffect(() => {
    dispatch({ type: 'SET_GROUPS', payload: groups });
  }, [groups]);
  useEffect(() => {
    dispatch({ type: 'SET_STUDENTS', payload: students });
  }, [students]);
  useEffect(() => {
    dispatch({ type: 'SET_PAYMENT_HISTORY', payload: paymentHistory });
  }, [paymentHistory]);
  useEffect(() => {
    dispatch({ type: 'SET_BRANCHES', payload: branches });
  }, [branches]);

  // ----- Active branch check -----
  useEffect(() => {
    if (branches.length === 0) return;

    let branchIdToSet = state.activeBranchId;
    const branchExists = branches.some(
      (branch) => branch.id === state.activeBranchId
    );

    if (!branchExists) {
      const defaultBranch = branches.find((branch) => branch.id === 'default');
      branchIdToSet = defaultBranch ? 'default' : branches[0].id;
    }

    dispatch({ type: 'SET_BRANCHES', payload: branches });
    dispatch({ type: 'SET_ACTIVE_BRANCH', payload: branchIdToSet });
  }, [branches]);

  // ----- Actions -----
  const setActiveBranch = (branchId) => {
    if (branchId) {
      localStorage.setItem('activeBranchId', branchId);
    } else {
      localStorage.removeItem('activeBranchId');
    }
    dispatch({ type: 'SET_ACTIVE_BRANCH', payload: branchId });
  };

  const addRoom = (room) => {
    dispatch({ type: 'SET_ROOMS', payload: [...state.rooms, room] });
  };

  const logoutUser = () => signOut(auth);

  const toggleModal = (modal, value) =>
    dispatch({ type: 'TOGGLE_MODAL', payload: { modal, value } });

  // ----- Context value -----
  const contextValue = useMemo(
    () => ({
      ...state,
      logoutUser,
      toggleModal,
      setActiveBranch,
      addRoom,
    }),
    [state]
  );

  return (
    <MainContext.Provider value={contextValue}>{children}</MainContext.Provider>
  );
};
